/**
 * Discount-aware helpers shared between cart-drawer.tsx and cart/page.tsx (Task 14
 * review fix-round). Both files gate something (the free-shipping meter, the shipping
 * row's "free vs Richtwert" decision) on the discounted merchandise subtotal, and both
 * feed src/checkout/lib/utils/resolve-checkout-gross-total.ts's `resolveCheckoutGrossTotal`
 * (imported, never edited) to reconcile the displayed "Gesamt" against Saleor's real total.
 *
 * That function's own branch condition -- whether a checkout-level voucher discount is
 * already distributed ("folded") into the line prices, or still needs subtracting
 * separately -- is re-derived here so callers of resolveFreeShippingBasis and callers of
 * resolveCheckoutGrossTotal can never disagree about which case they are in.
 */

/** Structurally matches both the Saleor-generated checkout line and the looser cart-drawer prop. */
type DiscountableLine = {
	quantity: number;
	totalPrice: { gross: { amount: number } };
	variant: {
		pricing?: {
			priceUndiscounted?: { gross?: { amount: number } | null } | null;
		} | null;
	};
};

/**
 * Sums (undiscounted unit price x qty - actual line total) across lines: how much of a
 * per-line SALE discount is already baked into the price Saleor charges for that line.
 * This is the `lineDiscountInPrices` input `resolveCheckoutGrossTotal` expects.
 */
export function sumLineDiscountInPrices(lines: DiscountableLine[]): number {
	return lines.reduce((sum, line) => {
		const undiscountedUnit = line.variant.pricing?.priceUndiscounted?.gross?.amount;
		if (undiscountedUnit == null) {
			return sum;
		}
		const lineUndiscounted = undiscountedUnit * line.quantity;
		const actual = line.totalPrice.gross.amount;
		return sum + Math.max(0, lineUndiscounted - actual);
	}, 0);
}

/** NaN/Infinity in, 0 out -- both `isDiscountFoldedIntoLines` and `resolveFreeShippingBasis`
 *  take numbers assembled from optional GraphQL fields (`checkout.discount?.amount`,
 *  a reduce over line prices); a non-finite value must not propagate into `Math.abs`/
 *  subtraction and produce a NaN threshold decision. Today's callers happen to already guard
 *  upstream, which is exactly why this was only "accidentally" safe -- guard here too, so the
 *  module is correct standalone, not just for its current callers. */
function safeNumber(n: number): number {
	return Number.isFinite(n) ? n : 0;
}

/**
 * Mirrors resolveCheckoutGrossTotal's private `approxEqual(lineDiscountInPrices, discount,
 * DEFAULT_EPSILON)` check (epsilon not exported from that module, so pinned here at the same
 * value): true when the checkout-level voucher discount is already reflected in the line
 * prices Saleor charges, i.e. `merchandiseSubtotal` already accounts for it.
 */
export function isDiscountFoldedIntoLines(discount: number, lineDiscountInPrices: number): boolean {
	const EPSILON = 0.02;
	const safeDiscount = safeNumber(discount);
	const safeLineDiscountInPrices = safeNumber(lineDiscountInPrices);
	return safeDiscount > 0 && Math.abs(safeLineDiscountInPrices - safeDiscount) <= EPSILON;
}

/**
 * Discount-aware basis for the free-shipping meter and the shipping-row "free vs estimate"
 * decision.
 *
 * When the voucher is NOT folded into line prices, `merchandiseSubtotal` is still the
 * undiscounted figure -- gating a free-shipping promise on it directly would let the UI claim
 * "free!" at e.g. 255 - 20 = 235 (below Saleor's discounted maxOrder check), a promise Saleor
 * then does not grant at checkout. Subtracting the discount here is the conservative fix:
 * worst case it under-promises (shows a Richtwert Saleor would in fact have waived), never the
 * reverse.
 */
export function resolveFreeShippingBasis(
	merchandiseSubtotal: number,
	discount: number,
	lineDiscountInPrices: number,
): number {
	const safeDiscount = safeNumber(discount);
	const safeLineDiscountInPrices = safeNumber(lineDiscountInPrices);
	if (safeDiscount > 0 && !isDiscountFoldedIntoLines(safeDiscount, safeLineDiscountInPrices)) {
		return Math.max(0, merchandiseSubtotal - safeDiscount);
	}
	return merchandiseSubtotal;
}
